import { call, put, select, takeLatest } from 'redux-saga/effects'
import { get, set, cloneDeep, isString, isEmpty, isEqual } from 'lodash'

import Stadium, {
  loadConfig,
  loadConfigSucceeded,
  loadConfigFailed,
  createConfig,
  createConfigSucceeded,
  createConfigFailed,
  updateConfig,
  updateConfigSucceeded,
  updateConfigFailed,
} from '@ordernext/networking-stadium/stadium'

import { REFRESH_CONFIG } from '../actions/config'
import { getDeviceId } from '../utils/deviceData'

import { currentMenuSelected, menusRequested, menusItemsRequested } from '../actions/menus'

import { getCurrentConfiguration } from '../selectors/config'

import Environment from '../utils/environment'
import { deviceAuthenticated, saveConfig } from '../utils/bridgeMethods'
import { toast } from 'react-toastify'
import { clearCart } from '../actions/cart'
import { clearPromotionsOnOrder } from '../actions/promotion'
import { customHistory } from '../stores'

const syncPrintControllerWithBridge = (response) => {
  // If the `controllerPrinterUuids` is present and not an empty array, this is a print controller.
  let controlledPrinterUuids = get(response, 'data.appDeviceConfigurations[0].configuration.controlledPrinterUuids')

  if (controlledPrinterUuids === undefined) {
    controlledPrinterUuids = get(response, 'data.appDeviceConfiguration.configuration.controlledPrinterUuids')
  }

  const isPrintController = (controlledPrinterUuids?.length ?? 0) > 0

  deviceAuthenticated({ isPrintController })
}

const formatDeviceSubtype = (deviceSubtype) => {
  if (deviceSubtype === 'concessions-pos') return 'Concessions'
  if (deviceSubtype === 'kiosk') return 'Kiosk'
  if (deviceSubtype === 'tabs-pos') return 'Tabs'
  if (deviceSubtype === 'suites-pos') return 'Suites'
}

const configsAreEqual = (config, newParams) => {
  const propertyAssertions = [
    ['deviceSubtype', 'payload.deviceSubtype'],
    ['configuration.menuUuids', 'payload.menuUuids'],
    ['configuration.terminalId', 'payload.terminalId'],
    ['configuration.storeId', 'payload.storeId'],
    ['configuration.esKey', 'payload.esKey'],
  ]

  return propertyAssertions.every(([pathOne, pathTwo]) => {
    return isEqual(get(config, pathOne), get(newParams, pathTwo))
  })
}

const directTo = (method = 'push', path) => {
  if (path === undefined) return

  customHistory[method](path)
}

export function* reloadMenus() {
  yield put(menusRequested(true))
  yield put(menusItemsRequested(true))

  const currentConfig = yield select(getCurrentConfiguration)
  const menuUuids = get(currentConfig, 'configuration.menuUuids', [])

  if (menuUuids.length === 1) yield put(currentMenuSelected(menuUuids[0]))
}

let errorToast = undefined

function* getConfigRequest(id) {
  const venueUuid = process.env.REACT_APP_VENUE_UUID
  const deviceId = getDeviceId()
  try {
    const result = yield call(Stadium.getConfig, id, venueUuid, deviceId)
    let deviceSubtype = get(result, 'data.appDeviceConfigurations[0].deviceSubtype')

    if (!deviceSubtype) {
      deviceSubtype = get(result, 'data.appDeviceConfiguration.deviceSubtype')
    }

    if (deviceSubtype && !['tabs-pos'].includes(deviceSubtype)) {
      yield put(loadConfigSucceeded(result))
      yield call(directTo, 'push', '/revenue-center-setup')
      if (!toast.isActive(errorToast)) {
        errorToast = toast.error(`Expected Tabs device type but received: ${formatDeviceSubtype(deviceSubtype)}`)
      }

      return false
    }

    syncPrintControllerWithBridge(result)

    // TODO: (@westinschepper): Use this when we opt in to passing all data to the APK
    // const appDeviceConfig = get(result, 'data.appDeviceConfiguration', get(result, 'data.appDeviceConfigurations[0]', {}))
    // const flattenedAppDeviceConfig = {
    //   ...appDeviceConfig,
    //   ...appDeviceConfig.configuration,
    // }

    let max = get(result, 'data.appDeviceConfigurations[0].orderNumberEnd')
    let min = get(result, 'data.appDeviceConfigurations[0].orderNumberStart')
    let configuration = get(result, 'data.appDeviceConfigurations[0].configuration', {})
    let { terminalId, storeId, esKey, prodUrl, uatUrl, host } = configuration

    /**
     * TODO:
     * We should ask about getting this changed.
     * if there is only one configuration, Stadium returns a singular key name.
     */
    if (!max) max = get(result, 'data.appDeviceConfiguration.orderNumberEnd')
    if (!min) min = get(result, 'data.appDeviceConfiguration.orderNumberStart')
    if (!host) host = get(result, 'data.appDeviceConfiguration.configuration.host')
    if (!storeId) storeId = get(result, 'data.appDeviceConfiguration.configuration.storeId')
    if (!terminalId) terminalId = get(result, 'data.appDeviceConfiguration.configuration.terminalId')
    if (!esKey) esKey = get(result, 'data.appDeviceConfiguration.configuration.esKey')
    if (!prodUrl) prodUrl = get(result, 'data.appDeviceConfiguration.configuration.prodUrl', 'https://cs.freedompay.us/Fasta/')
    if (!uatUrl) uatUrl = get(result, 'data.appDeviceConfiguration.configuration.uatUrl', 'https://cs.uat.freedompay.com/Fasta/')

    localStorage.setItem('orderNumberMax', max)
    localStorage.setItem('orderNumberMin', min)

    // FIXME: Update this when the URL's are coming back as expected from Stadium
    let url = uatUrl

    if (Environment.app.isProduction) {
      url = prodUrl
    }

    const deviceConfig = {
      storeID: storeId,
      terminalID: terminalId,
      esKey,
      host,
      url,
    }

    saveConfig(deviceConfig)

    yield put(loadConfigSucceeded(result))
    yield reloadMenus()
    return result
  } catch (err) {
    yield put(loadConfigFailed(err))
    throw err
  }
}

export function* getConfig() {
  try {
    // Not providing a record id ensures we always get the latest configuration.
    yield call(getConfigRequest, undefined)
  } catch {}
}

function* refreshConfigSaga() {
  try {
    yield put(clearCart())
    yield put(clearPromotionsOnOrder())
    yield call(getConfigRequest, undefined)
    toast.success('Config refreshed successfully')
  } catch (err) {
    toast.error('Failed to refresh config')
  }
}

export function* watchGetConfig() {
  yield takeLatest(loadConfig.type, getConfig)
}

export function* watchRefreshConfigSaga() {
  yield takeLatest(REFRESH_CONFIG, refreshConfigSaga)
}

export function* _createConfig(params = {}) {
  try {
    const menuUuids = get(params, 'payload.menuUuids', [])
    const deviceSubtype = get(params, 'payload.deviceSubtype')

    const body = {
      appDeviceConfigurations: [
        {
          configuration: {
            menuUuids: menuUuids
          },
          deviceSubtype,
          organizationName: process.env.REACT_APP_ORG,
          venueUuid: process.env.REACT_APP_VENUE_UUID,
        },
      ]
    }

    const result = yield call(Stadium.createConfig, body)

    syncPrintControllerWithBridge(result)

    const max = get(result, 'data.appDeviceConfigurations[0].orderNumberEnd')
    const min = get(result, 'data.appDeviceConfigurations[0].orderNumberStart')
    localStorage.setItem('orderNumberMax', max)
    localStorage.setItem('orderNumberMin', min)

    yield put(createConfigSucceeded(result))
    yield reloadMenus()
    yield call(getConfigRequest, undefined)
  } catch (err) {
    yield put(createConfigFailed(err))
  }
}

export function* watchCreateConfig() {
  yield takeLatest(createConfig.type, _createConfig)
}

export function* _updateConfig(params = {}) {
  try {
    const menuUuids = get(params, 'payload.menuUuids')
    const terminalId = get(params, 'payload.terminalId')
    const storeId = get(params, 'payload.storeId')
    const esKey = get(params, 'payload.esKey')
    const deviceSubtype = get(params, 'payload.deviceSubtype')
    const currentConfiguration = yield select((state) => get(state, 'config'))
    const clone = cloneDeep(currentConfiguration)

    if (!isEmpty(menuUuids)) {
      set(clone, 'configuration.menuUuids', menuUuids)
    }

    if (deviceSubtype) {
      set(clone, 'deviceSubtype', deviceSubtype)
    }

    if (terminalId && storeId && esKey) {
      set(clone, 'configuration.terminalId', terminalId)
      set(clone, 'configuration.storeId', storeId)
      set(clone, 'configuration.esKey', esKey)
    }

    delete clone.hasConfig
    delete clone.menuUuid
    delete clone.remote

    const body = {
      appDeviceConfigurations: [clone],
      uuid: get(currentConfiguration, 'uuid'),
      venueUuid: process.env.REACT_APP_VENUE_UUID
    }

    if (configsAreEqual(currentConfiguration, params)) {
      // This success action just merges, so it is safe to pass an empty object.
      yield put(updateConfigSucceeded({}))
      yield call(getConfigRequest, undefined)
      yield reloadMenus()
      return
    }

    const result = yield call(Stadium.updateConfig, body)

    syncPrintControllerWithBridge(result)

    const max = get(result, 'data.appDeviceConfigurations[0].orderNumberEnd')
    const min = get(result, 'data.appDeviceConfigurations[0].orderNumberStart')
    localStorage.setItem('orderNumberMax', max)
    localStorage.setItem('orderNumberMin', min)

    yield put(updateConfigSucceeded(result))
    yield call(getConfigRequest, undefined)
    yield reloadMenus()
  } catch (err) {
    yield put(updateConfigFailed(err))
  }
}

export function* watchUpdateConfig() {
  yield takeLatest(updateConfig.type, _updateConfig)
}
